import { getUrl } from "aws-amplify/storage";
import { CheckIcon, XIcon } from "@heroicons/react/solid";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat, PatternFormat } from "react-number-format";
import { useNavigate, useParams } from "react-router-dom";
import { Alert } from "../../Components/Alert";
import DetailSkeleton from "../../Components/DetailSkeleton";
import { addToCart } from "../../Context/checkout/actions";
import { useCheckoutDispatch } from "../../Context/checkout/context";
import {
  requestGetEbilling,
  setEbillingInitialData,
} from "../../Context/ebilling/actions";
import {
  useEbillingDispatch,
  useEbillingState,
} from "../../Context/ebilling/context";
import { classNames, convertToGoogleAnalyticsItem, trackAddToCart } from "../../Utils";
import { analytics } from "../../firebase";
import { logEvent } from "firebase/analytics";
import { CSButton } from "../../Components/CSButton";
import Badge from "../../Components/Badge";
import MaintenanceModal from "../../Components/MaintenanceModal";
import { useRemoteConfig } from "../../Context/firebase/context";

const initialSteps = [
  {
    name: "E-Billing Code",
    description: "E-Billing Code Created",
    status: "current",
  },
  {
    name: "Payment",
    description: "Payment Processed",
    status: "upcoming",
  },
  {
    name: "NTB & NTPN",
    description: "Bank and Tax Transaction Number Issued",
    status: "upcoming",
  },
  {
    name: "BPN",
    description: "Tax Receipt Ready for Download",
    status: "upcoming",
  },
];

export const EbillingDetail = () => {
  const { id } = useParams();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const checkoutDispatch = useCheckoutDispatch();

  const dispatch = useEbillingDispatch();
  const { ebilling, loading, error } = useEbillingState();

  const { ebilling_payment: ebillingPaymentConfig, loading: configLoading } =
    useRemoteConfig();

  const [steps, setsteps] = useState([]);

  const [ebillingPaymentEnabled, setebillingPaymentEnabled] = useState(true);
  const [ebillingPaymentStatus, setebillingPaymentStatus] = useState("");
  const [
    ebillingPaymentMaintenanceVisible,
    setebillingPaymentMaintenanceVisible,
  ] = useState(false);

  const isInputBilling =
    !Boolean(ebilling?.kap?.id && ebilling?.kjs?.id) &&
    Boolean(ebilling?.billingId);

  useEffect(() => {
    requestGetEbilling(dispatch, { id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (ebilling?.status) {
      let updatedSteps = initialSteps;
      if (ebilling.status === "PENDING_VERIFICATION" && ebilling.billingId) {
        updatedSteps[0]["status"] = "current";
        updatedSteps[0]["description"] = "pendingVerificationLabel";
        updatedSteps[1]["status"] = "upcoming";
        updatedSteps[2]["status"] = "upcoming";
        updatedSteps[3]["status"] = "upcoming";
      } else if (ebilling.status === "PENDING" && !ebilling.billingId) {
        updatedSteps[0]["status"] = "current";
        updatedSteps[0]["description"] = "Waiting for E-Billing Code";
        updatedSteps[1]["status"] = "upcoming";
        updatedSteps[2]["status"] = "upcoming";
        updatedSteps[3]["status"] = "upcoming";
      } else if (ebilling.status === "PENDING" && ebilling.billingId) {
        updatedSteps[0]["status"] = "complete";
        updatedSteps[1]["status"] = "current";
        updatedSteps[1]["description"] = "Waiting for Payment";
        updatedSteps[2]["status"] = "upcoming";
        updatedSteps[3]["status"] = "upcoming";
      } else if (ebilling.status === "SUCCESS" && !ebilling.output?.ntb) {
        updatedSteps[0]["status"] = "complete";
        updatedSteps[1]["status"] = "complete";
        updatedSteps[2]["status"] = "current";
        updatedSteps[2]["description"] = "Waiting for NTB & NTPN";
        updatedSteps[3]["status"] = "upcoming";
      } else if (
        ebilling.status === "SUCCESS" &&
        ebilling.output?.ntb &&
        !ebilling.output?.bpn
      ) {
        updatedSteps[0]["status"] = "complete";
        updatedSteps[1]["status"] = "complete";
        updatedSteps[2]["status"] = "complete";
        updatedSteps[3]["status"] = "current";
        updatedSteps[3]["description"] = "Waiting for BPN Document";
      } else {
        updatedSteps[0]["status"] = "complete";
        updatedSteps[1]["status"] = "complete";
        updatedSteps[2]["status"] = "complete";
        updatedSteps[3]["status"] = "complete";
      }
      setsteps(updatedSteps);
    }
  }, [ebilling]);

  const onPay = () => {
    if (!ebillingPaymentEnabled) {
      setebillingPaymentMaintenanceVisible(true);
    } else {
      const checkoutEbillings = [];

      let name = `Pembayaran E-Billing ${ebilling.billingId}`;
      if (ebilling.kap?.name && ebilling.kjs?.name) {
        name = `${ebilling.kap.name} ${ebilling.kjs.name}`;
      }

      let category = ebilling.billingId;
      if (ebilling.startPeriod && ebilling.endPeriod && ebilling.year) {
        category = `${ebilling.startPeriod}${
          ebilling.startPeriod === ebilling.endPeriod
            ? ""
            : `-${ebilling.endPeriod}`
        }/${ebilling.year}`;
      }

      checkoutEbillings.push({
        id: ebilling.id,
        type: "ebilling",
        name: name,
        detail: {
          category,
          description: ebilling.summary?.npwp,
        },
        price: ebilling.total,
        quantity: 1,
      });

      const analyticsParams = {
        currency: "IDR",
        value: checkoutEbillings.reduce((total, product) => {
          return total + product.price * product.quantity;
        }, 0),
        items: checkoutEbillings.map((product) =>
          convertToGoogleAnalyticsItem(product)
        ),
      };
      logEvent(analytics, "add_to_cart", analyticsParams);
      trackAddToCart({ products: checkoutEbillings });

      addToCart(checkoutDispatch, {
        products: checkoutEbillings,
      });

      navigate("/checkout");
    }
  };

  const downloadFile = async (key) => {
    try {
      const urlResult = await getUrl({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: {
          useAccelerateEndpoint: true,
        },
      });
      const signedURL = urlResult.url.href;
      window.open(signedURL);
    } catch (error) {}
  };

  const renderStep = (step, stepIdx) => {
    if (step.status === "current") {
      return (
        <div>
          {stepIdx !== steps.length - 1 ? (
            <div
              className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-gray-300"
              aria-hidden="true"
            />
          ) : null}
          <div className="relative flex items-start group" aria-current="step">
            <span className="h-9 flex items-center" aria-hidden="true">
              <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-blue-600 rounded-full">
                <span className="h-2.5 w-2.5 bg-blue-600 rounded-full" />
              </span>
            </span>
            <span className="ml-4 min-w-0 flex flex-col">
              <span className="text-xs font-semibold tracking-wide uppercase text-blue-600">
                {t(step.name)}
              </span>
              <span className="text-sm text-gray-500">
                {t(step.description)}
              </span>
            </span>
          </div>
        </div>
      );
    }
    if (step.status === "complete") {
      return (
        <div>
          {stepIdx !== steps.length - 1 ? (
            <div
              className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-blue-600"
              aria-hidden="true"
            />
          ) : null}
          <div className="relative flex items-start group">
            <span className="h-9 flex items-center">
              <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-blue-600 rounded-full">
                <CheckIcon className="w-5 h-5 text-white" aria-hidden="true" />
              </span>
            </span>
            <span className="ml-4 min-w-0 flex flex-col">
              <span className="text-xs font-semibold tracking-wide uppercase">
                {t(step.name)}
              </span>
              <span className="text-sm text-gray-500">
                {t(step.description)}
              </span>
            </span>
          </div>
        </div>
      );
    }
    if (step.status === "failed") {
      return (
        <div>
          {stepIdx !== steps.length - 1 ? (
            <div
              className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-red-600"
              aria-hidden="true"
            />
          ) : null}
          <div className="relative flex items-start group">
            <span className="h-9 flex items-center">
              <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-red-600 rounded-full">
                <XIcon className="w-5 h-5 text-white" aria-hidden="true" />
              </span>
            </span>
            <span className="ml-4 min-w-0 flex flex-col">
              <span className="text-xs font-semibold tracking-wide uppercase">
                {t(step.name)}
              </span>
              <span className="text-sm text-gray-500">
                {t(step.description)}
              </span>
            </span>
          </div>
        </div>
      );
    }
    return (
      <div>
        {stepIdx !== steps.length - 1 ? (
          <div
            className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-gray-300"
            aria-hidden="true"
          />
        ) : null}
        <div className="relative flex items-start group">
          <span className="h-9 flex items-center" aria-hidden="true">
            <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-gray-300 rounded-full"></span>
          </span>
          <span className="ml-4 min-w-0 flex flex-col">
            <span className="text-xs font-semibold tracking-wide uppercase text-gray-500">
              {t(step.name)}
            </span>
            <span className="text-sm text-gray-500">{t(step.description)}</span>
          </span>
        </div>
      </div>
    );
  };

  const renderMaintenanceBadge = () => {
    const isUnavailable =
      ebillingPaymentStatus === "OFFLINE" ||
      ebillingPaymentStatus === "SCHEDULED_OFFLINE" ||
      ebillingPaymentStatus === "SCHEDULED_ACTIVE";

    const badgeValue = isUnavailable ? "unavailable" : "maintenance";
    const badgeStyle = isUnavailable ? "secondary" : "alert";

    return (
      <div className="flex items-center">
        <Badge title={badgeValue} type={badgeStyle} />
      </div>
    );
  };

  if (!ebilling) {
    return (
      <div className="flex flex-col gap-6">
        <div className="bg-white min-h-full rounded-lg p-4">
          <Alert
            type="error"
            title="Error"
            message={error?.message || "E-Billing not found."}
          />
        </div>
        <div className="bg-white p-4 rounded-lg">
          <CSButton />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <MaintenanceModal
        isVisible={ebillingPaymentMaintenanceVisible}
        onClose={() => setebillingPaymentMaintenanceVisible(false)}
        onChangeAvailability={(available) =>
          setebillingPaymentEnabled(available)
        }
        onChangeStatus={(status) => setebillingPaymentStatus(status)}
        configValue={ebillingPaymentConfig}
      />
      <div className="max-w-3xl grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="lg:col-start-3 lg:col-span-1">
          {loading ? (
            <div className="bg-white animate-pulse rounded-lg p-4 flex items-center">
              <div className="h-8 w-8 mr-2 bg-gray-300 rounded-full"></div>
              <div>
                <div className="h-4 w-24 my-1 bg-gray-300 rounded-md"></div>
                <div className="h-4 w-48 my-1 bg-gray-300 rounded-md"></div>
              </div>
            </div>
          ) : (
            <div>
              {(steps.length > 0 || ebilling?.status === "FAILED") && (
                <div className="bg-white rounded-lg p-4">
                  {(ebilling?.status === "PENDING_VERIFICATION" ||
                    ebilling?.status === "PENDING" ||
                    ebilling?.status === "SUCCESS") && (
                    <ol className="overflow-hidden">
                      {steps.map((step, stepIdx) => (
                        <li
                          key={step.name}
                          className={classNames(
                            stepIdx !== steps.length - 1 ? "pb-10" : "",
                            "relative"
                          )}
                        >
                          {renderStep(step, stepIdx)}
                        </li>
                      ))}
                    </ol>
                  )}
                  {ebilling?.status === "FAILED" && (
                    <div className="relative flex items-start group">
                      <span className="h-9 flex items-center">
                        <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-red-600 rounded-full">
                          <XIcon
                            className="w-5 h-5 text-white"
                            aria-hidden="true"
                          />
                        </span>
                      </span>
                      <span className="ml-4 min-w-0 flex flex-col">
                        <span className="text-xs font-semibold tracking-wide uppercase">
                          {t("Error")}
                        </span>
                        <span className="text-sm text-gray-500">
                          {t(ebilling.error || "E-Billing Error")}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="bg-white rounded-lg lg:col-start-1 lg:col-span-2">
          <div className="px-4 py-5 sm:px-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Tax Account Code (KAP)")}
                </dt>
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {ebilling.kap?.id || "-"}
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Tax Payment Code (KJS)")}
                </dt>
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {ebilling.kjs?.kjsId || "-"}
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Period")}
                </dt>
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {ebilling.year
                      ? `${ebilling.startPeriod}${
                          ebilling.endPeriod !== ebilling.startPeriod
                            ? ` - ${ebilling.endPeriod}`
                            : ""
                        } / ${ebilling.year}`
                      : "-"}
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Tax Identification Number (NPWP)")}
                </dt>
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {ebilling.summary?.npwp ? (
                      <NumericFormat
                        value={ebilling.summary.npwp}
                        displayType={"text"}
                      />
                    ) : (
                      `${
                        ebilling.status === "PENDING_VERIFICATION"
                          ? t("pendingVerificationLabel")
                          : t("Failed")
                      }`
                    )}
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Total")}
                </dt>
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {ebilling.total ? (
                      <NumericFormat
                        displayType={"text"}
                        value={ebilling.total}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        prefix={"Rp"}
                      />
                    ) : (
                      `${
                        ebilling.status === "PENDING_VERIFICATION"
                          ? t("pendingVerificationLabel")
                          : t("Failed")
                      }`
                    )}
                  </dd>
                )}
              </div>
              {(!ebilling.kap?.id || !ebilling.kjs?.id) &&
                ebilling.summary?.name && (
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("taxpayerName")}
                    </dt>
                    {loading ? (
                      <DetailSkeleton />
                    ) : (
                      <dd className="mt-1 text-sm text-gray-900">
                        {ebilling.summary.name}
                      </dd>
                    )}
                  </div>
                )}
              {ebilling.business && (
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("businessProfile")}
                  </dt>
                  {loading ? (
                    <DetailSkeleton />
                  ) : (
                    <dd className="mt-1 text-sm text-gray-900">
                      <a
                        className="text-sm text-blue-600 underline"
                        href={`/ebilling?business=${ebilling.business.id}`}
                      >
                        {ebilling.business.name}
                      </a>
                    </dd>
                  )}
                </div>
              )}
              {ebilling.collectorNpwp && (
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("Tax Subject")}
                  </dt>
                  {loading ? (
                    <DetailSkeleton />
                  ) : (
                    <dd className="mt-1 text-sm text-gray-900">
                      {ebilling.collectorNpwp ? (
                        <NumericFormat
                          value={ebilling.collectorNpwp}
                          displayType={"text"}
                        />
                      ) : (
                        "-"
                      )}
                    </dd>
                  )}
                </div>
              )}
              {ebilling.sk && (
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("Notice (SK) Number")}
                  </dt>
                  {loading ? (
                    <DetailSkeleton />
                  ) : (
                    <dd className="mt-1 text-sm text-gray-900">
                      {ebilling.sk ? (
                        <PatternFormat
                          value={ebilling.sk}
                          displayType={"text"}
                          format="#####/###/##/###/##"
                          mask="_"
                        />
                      ) : (
                        "-"
                      )}
                    </dd>
                  )}
                </div>
              )}
              {ebilling.nop && (
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("Tax Object Number (NOP)")}
                  </dt>
                  {loading ? (
                    <DetailSkeleton />
                  ) : (
                    <dd className="mt-1 text-sm text-gray-900">
                      {ebilling.nop ? (
                        <PatternFormat
                          value={ebilling.nop}
                          displayType={"text"}
                          format="##.##.###.###.###.####.#"
                          mask="_"
                        />
                      ) : (
                        "-"
                      )}
                    </dd>
                  )}
                </div>
              )}
            </dl>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <div className="flex gap-2">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("Payment")}
                  </dt>
                  {!ebillingPaymentEnabled && renderMaintenanceBadge()}
                </div>
                {loading || configLoading ? (
                  <DetailSkeleton />
                ) : (
                  <div className="mt-1">
                    {ebilling.status === "PENDING" && ebilling.billingId && (
                      <button onClick={onPay}>
                        <span className="text-sm text-blue-600 underline">
                          {t("Pay Now")}
                        </span>
                      </button>
                    )}
                    {ebilling.status === "PENDING" && !ebilling.billingId && (
                      <span className="text-sm text-gray-900">
                        {t("Waiting for E-Billing Code")}
                      </span>
                    )}
                    {ebilling.status === "PENDING_VERIFICATION" &&
                      ebilling.billingId && (
                        <span className="text-sm text-gray-900">
                          {t("pendingVerificationLabel")}
                        </span>
                      )}
                    {ebilling.status === "FAILED" &&
                      !ebilling.transactionID && (
                        <span className="text-sm text-gray-900">-</span>
                      )}
                    {ebilling.transactionID && (
                      <button
                        onClick={() =>
                          navigate(`/transaction/${ebilling.transactionID}`)
                        }
                        className="text-sm text-blue-600 underline"
                      >
                        {ebilling.transactionID.slice(-5).toUpperCase()}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("E-Billing Code")}
                </dt>
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <div>
                    {ebilling.status === "FAILED" ? (
                      <dd className="mt-1 text-sm text-gray-900">-</dd>
                    ) : (
                      <dd className="mt-1 text-sm text-gray-900">
                        {ebilling.billingId || t("On Process")}
                      </dd>
                    )}
                  </div>
                )}
              </div>
              {ebilling.output && ebilling.status === "SUCCESS" && (
                <>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("NTB")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {ebilling.output?.ntb}
                    </dd>
                  </div>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("NTPN")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {ebilling.output?.ntpn}
                    </dd>
                  </div>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("BPN")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {ebilling.output?.bpn?.key ? (
                        <button
                          onClick={() => {
                            downloadFile(ebilling.output.bpn.key);
                          }}
                        >
                          <span className="text-sm text-blue-600 underline">
                            {t("Download")}
                          </span>
                        </button>
                      ) : (
                        <span>{t("On Process")}</span>
                      )}
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>
          {ebilling.status === "FAILED" && ebilling.error && (
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("Action")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <button
                      onClick={() => {
                        if (isInputBilling) {
                          navigate(`/ebilling/input?id=${ebilling.billingId}`);
                        } else {
                          setEbillingInitialData(dispatch, {
                            initialData: ebilling,
                          });
                          navigate(
                            `/ebilling/create?kap=${ebilling?.kap?.id}${
                              ebilling?.kjs?.kjsId
                                ? `&kjs=${ebilling?.kjs?.kjsId}`
                                : ""
                            }`
                          );
                        }
                      }}
                    >
                      <span className="text-sm text-blue-600 underline">
                        {t("retry")}
                      </span>
                    </button>
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      </div>
      <div className="max-w-3xl grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="col-span-2 bg-white p-4 rounded-lg">
          <CSButton />
        </div>
      </div>
    </div>
  );
};
